import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const groupByYear = (lives) => {
  const map = new Map();
  for (const l of lives) {
    const y = Number((l.date || "").slice(0, 4)); // timezone-safe: use ISO year segment
    if (!y) continue;
    if (!map.has(y)) map.set(y, []);
    map.get(y).push(l);
  }
  return Array.from(map.entries()).sort((a, b) => b[0] - a[0]); // descending
};

const LiveRow = ({ live, index, testPrefix }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.03 }}
    className="grid md:grid-cols-[220px_1fr_auto] items-center gap-4 md:gap-8 p-5 md:p-6 border border-white/10 bg-[color:var(--bg-panel)] hover:border-[color:var(--alien)]"
    data-testid={`${testPrefix}-${index}`}
  >
    <div className="font-display text-3xl text-[color:var(--alien)]">{formatDate(live.date).toUpperCase()}</div>
    <div>
      <h3 className="font-display text-2xl md:text-3xl">{live.venue}</h3>
      <p className="text-white/60 text-sm flex items-center gap-2 mt-1">
        <MapPin size={14} /> {live.address}{live.address && live.city ? ", " : ""}{live.city}
      </p>
      {live.notes && <p className="text-white/40 text-xs mt-1">{live.notes}</p>}
    </div>
    {live.map_url && (
      <a href={live.map_url} target="_blank" rel="noreferrer" data-testid={`${testPrefix}-${index}-map`}
        className="border border-white/30 px-4 py-2 text-xs uppercase tracking-widest hover:border-[color:var(--alien)] hover:text-[color:var(--alien)]">
        Mappa
      </a>
    )}
  </motion.div>
);

export default function LiveCalendar() {
  const [lives, setLives] = useState([]);
  const [filter, setFilter] = useState("upcoming"); // upcoming | archive | all

  useEffect(() => {
    api.get("/lives").then(({ data }) => setLives(data)).catch(() => {});
  }, []);

  const nowIso = new Date().toISOString();

  const upcoming = useMemo(
    () => lives.filter((l) => l.date >= nowIso).sort((a, b) => a.date.localeCompare(b.date)),
    [lives, nowIso]
  );
  const past = useMemo(
    () => lives.filter((l) => l.date < nowIso).sort((a, b) => b.date.localeCompare(a.date)),
    [lives, nowIso]
  );
  const pastByYear = useMemo(() => groupByYear(past), [past]);

  const tabs = [
    { k: "upcoming", label: `In arrivo · ${upcoming.length}` },
    { k: "archive", label: `Archivio · ${past.length}` },
    { k: "all", label: `Tutti · ${lives.length}` },
  ];

  return (
    <div className="grain min-h-screen">
      <Navbar />

      <div className="pt-32 pb-24 px-5 md:px-10">
        <div className="max-w-6xl mx-auto">
          <p className="text-[color:var(--alien)] uppercase tracking-[0.4em] text-xs mb-3">Calendario</p>
          <h1 className="font-display text-6xl md:text-8xl mb-10">TUTTI I<br />CONCERTI</h1>

          <div className="flex gap-2 mb-10 border-b border-white/10 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.k}
                onClick={() => setFilter(t.k)}
                data-testid={`filter-${t.k}`}
                className={`px-5 py-3 uppercase tracking-widest text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${filter === t.k ? "text-[color:var(--alien)] border-[color:var(--alien)]" : "text-white/50 border-transparent hover:text-white"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {filter === "upcoming" && (
            upcoming.length === 0 ? (
              <p className="text-white/50 border border-dashed border-white/15 p-10 text-center" data-testid="calendar-empty">
                Nessun concerto in arrivo per ora. Stay tuned.
              </p>
            ) : (
              <div className="space-y-3" data-testid="upcoming-list">
                {upcoming.map((l, i) => <LiveRow key={l.id} live={l} index={i} testPrefix="upcoming-card" />)}
              </div>
            )
          )}

          {filter === "archive" && (
            past.length === 0 ? (
              <p className="text-white/50 border border-dashed border-white/15 p-10 text-center" data-testid="calendar-empty">
                Nessun concerto in archivio.
              </p>
            ) : (
              <div className="space-y-14">
                {pastByYear.map(([year, items]) => (
                  <section key={year} data-testid={`archive-year-${year}`}>
                    <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
                      <h2 className="font-display text-5xl md:text-7xl">
                        ARCHIVIO<br />
                        <span className="text-[color:var(--alien)]">LIVE {String(year).slice(-2)}</span>
                      </h2>
                      <p className="text-white/50 text-sm uppercase tracking-widest">{items.length} concert{items.length === 1 ? "o" : "i"}</p>
                    </div>
                    <div className="space-y-3">
                      {items.map((l, i) => <LiveRow key={l.id} live={l} index={i} testPrefix={`archive-${year}-card`} />)}
                    </div>
                  </section>
                ))}
              </div>
            )
          )}

          {filter === "all" && (
            <div className="space-y-14">
              {upcoming.length > 0 && (
                <section data-testid="all-upcoming">
                  <h2 className="font-display text-4xl md:text-5xl mb-6">IN ARRIVO</h2>
                  <div className="space-y-3">
                    {upcoming.map((l, i) => <LiveRow key={l.id} live={l} index={i} testPrefix="all-upcoming-card" />)}
                  </div>
                </section>
              )}
              {pastByYear.map(([year, items]) => (
                <section key={year} data-testid={`all-archive-year-${year}`}>
                  <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
                    <h2 className="font-display text-4xl md:text-5xl">
                      ARCHIVIO LIVE <span className="text-[color:var(--alien)]">{String(year).slice(-2)}</span>
                    </h2>
                    <p className="text-white/50 text-sm uppercase tracking-widest">{items.length} concert{items.length === 1 ? "o" : "i"}</p>
                  </div>
                  <div className="space-y-3">
                    {items.map((l, i) => <LiveRow key={l.id} live={l} index={i} testPrefix={`all-archive-${year}-card`} />)}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
